import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';

const usePrevious = (value: any): any => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

export enum PopoverStatus {
	Unset = 'unset',
	Opening = 'opening',
	Open = 'open',
	Closing = 'closing',
	Closed = 'closed',
}

interface PopoverProps {
	open: boolean;
	close: () => void;
	className?: string;
	position?: string;
	caret?: string;
	zIndex?: number;
	children: JSX.Element;
	waitForOpen?: boolean;
	userSignedIn?: boolean;
	popoverExtraTopMargin?: boolean;
	setPopoverStatus?: React.Dispatch<React.SetStateAction<PopoverStatus>>;
	waitForAnimation?: boolean;
}

export default function Popover(props: PopoverProps) {
	const {
		open,
		close,
		className,
		position,
		caret,
		zIndex,
		children,
		waitForOpen,
		userSignedIn,
		popoverExtraTopMargin,
		setPopoverStatus,
		waitForAnimation = false,
	} = props;

	const [showPlaceholder, setShowPlaceholder] = useState(true);
	const [animationState, setAnimationState] = useState('inactive');
	const [animateOut, setAnimateOut] = useState(false);
	const prev = usePrevious({ open });

	const closeAnimation = () => {
		setAnimationState('inactive');
	};

	function handleClick(e: any) {
		if (e && e.composedPath) {
			let keepOpen = false;
			const path = e.composedPath();
			const len = path.length;

			for (let i = 0; i < len; i += 1) {
				if (typeof path[i].className === 'string' && path[i].className.includes('popover')) {
					keepOpen = true;
					break;
				}
			}

			if (!keepOpen) closeAnimation();
		}
	}

	useEffect(() => {
		if (prev && prev.open !== open) {
			if (open) {
				// Popover has become open after being closed; activate it immediately
				setTimeout(() => {
					setAnimationState('active');
					if (waitForAnimation) {
						setAnimateOut(true);
					}
				}, 50);
				window.addEventListener('mousedown', handleClick);
			} else {
				setAnimationState('inactive');
			}
		}

		return () => {
			window.removeEventListener('mousedown', handleClick);
		};
	}, [open]);

	const onAnimationComplete = (e: React.TransitionEvent<HTMLDivElement>) => {
		const target = e.target as HTMLDivElement;
		if (typeof target.className === 'string' && target.className.includes('inactive')) {
			setShowPlaceholder(true);
			close();
			setAnimateOut(false);
		} else if (waitForOpen) {
			setShowPlaceholder(false);
		}
	};

	// Set popover status based on animating out status and animation state
	// This makes the popover status available in a parent component
	useEffect(() => {
		if (!setPopoverStatus) return;

		if (!!setPopoverStatus && !waitForAnimation) {
			console.warn('Popover status will not be set because waitForAnimation is false');
		}

		if (animateOut && animationState === 'active') {
			// Since it opens immediately, we can skip opening and go right to open
			setPopoverStatus(PopoverStatus.Open);
		}

		if (animateOut && animationState === 'inactive') {
			setPopoverStatus(PopoverStatus.Closing);
		}

		if (!animateOut && animationState === 'inactive') {
			setPopoverStatus(PopoverStatus.Closed);
		}
	}, [
		animateOut,
		animationState,
		setPopoverStatus,
		waitForAnimation,
	]);

	return (
		/**
			Should show immediately when the open prop gets set to true, but the container needs
			to transition from inactive to active to play the animation. On close, we should
			set the animation state to 'inactive' so that it transitions out before completely closing
		*/
		open || animateOut ? (
			<>
				{' '}
				<div
					onTransitionEnd={onAnimationComplete}
					className={
						classNames(
							'popover',
							animationState,
							className,
							position,
							caret,
							userSignedIn ? 'user-signed-in' : '',
							popoverExtraTopMargin && 'extra-top-margin',
						)
					}
					style={zIndex ? { zIndex } : {}}
				>
					{/* eslint-disable no-nested-ternary */}
					{
						!waitForOpen
							? children
							: (showPlaceholder
								? (<div style={{ width: 340, height: 442 }}></div>)
								: children
							)
					}
				</div>

			</>
		) : (<></>)
	);
}
